import { useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button } from '@mui/material';
import { ExpandMore as ExpandIcon } from '@mui/icons-material';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { DiscoveryFormSection } from '../../../../types/advise';
import { useEngagementEditor } from './EngagementEditorContext';
import { DiscoveryQuestionField } from './DiscoveryQuestionField';
import { PanelHeader } from './PanelHeader';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { discoveryProgress, firstIncompleteSection, sectionProgress } from './DiscoveryPanel.calc';
import { StatusChip } from '../EngagementSteps';

const sectionElementId = (section: DiscoveryFormSection) => `discovery-section-${section.key}`;

export interface DiscoveryPanelProps {
  // Each new value opens the first section with a required question left and scrolls to it.
  focusIncomplete?: number;
}

export const DiscoveryPanel = ({ focusIncomplete = 0 }: DiscoveryPanelProps) => {
  const { canEdit, discovery } = useEngagementEditor();
  const { sections } = discovery;
  const [expanded, setExpanded] = useState<Set<string> | null>(null);
  const open = expanded ?? new Set(sections?.[0] ? [sections[0].key] : []);

  // A request made before this tab last mounted was already handled, so only a newer one counts.
  const handledFocus = useRef(focusIncomplete);

  useEffect(() => {
    if (focusIncomplete === handledFocus.current || !sections) return;
    handledFocus.current = focusIncomplete;
    const incomplete = firstIncompleteSection(sections);
    if (!incomplete) return;
    setExpanded((prev) => new Set([...(prev ?? open), incomplete.key]));
    document.getElementById(sectionElementId(incomplete))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusIncomplete]);

  if (discovery.failed) {
    return (
      <Alert
        severity="error"
        action={
          discovery.retry && (
            <Button
              color="inherit"
              size="small"
              onClick={discovery.retry}>
              Retry
            </Button>
          )
        }>
        Discovery could not be loaded.
      </Alert>
    );
  }

  if (!sections) {
    return <ComponentSpinner sx={{ padding: '3rem' }} />;
  }

  if (sections.length === 0) {
    return (
      <Alert severity="info">This engagement was created before the Discovery form existed, so it has none.</Alert>
    );
  }

  const totals = discoveryProgress(sections);
  const toggle = (key: string) => {
    const next = new Set(open);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpanded(next);
  };

  return (
    <Box data-testid="discovery-panel">
      <PanelHeader
        className="mb-4"
        title="Client Discovery"
        description="Capture the quantitative and qualitative needs of the client for this engagement."
      />
      <Box className="flex items-center justify-between mb-3">
        <TextSpan
          size="sm14"
          color="grey6">
          {`Discovery — ${sections.length} sections · ${totals.questions} questions total`}
        </TextSpan>
        <TextSpan
          size="sm14"
          weight="medium"
          color="grey8">
          {`${totals.answered} answered`}
        </TextSpan>
      </Box>
      {sections.map((section, index) => {
        const progress = sectionProgress(section);
        return (
          <Accordion
            key={section.key}
            id={sectionElementId(section)}
            expanded={open.has(section.key)}
            onChange={() => toggle(section.key)}
            disableGutters
            sx={{ mb: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandIcon />}
              sx={{ bgcolor: 'grey.100' }}>
              <Box className="flex flex-1 items-center gap-3 pr-2">
                <TextSpan
                  size="xs"
                  color="white"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-grey-8">
                  {index + 1}
                </TextSpan>
                <TextSpan
                  size="sm14"
                  weight="semibold"
                  color="grey8"
                  className="flex-1">
                  {section.label}
                </TextSpan>
                <TextSpan
                  size="xs"
                  color="grey6">
                  {`${progress.questions} question${progress.questions === 1 ? '' : 's'}`}
                </TextSpan>
                <StatusChip status={progress.status} />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {section.questions.map((question, position) => (
                <Box key={question.id}>
                  {question.group && question.group !== section.questions[position - 1]?.group && (
                    <TextBlock
                      size="sm14"
                      weight="semibold"
                      color="indigo"
                      className="mt-4">
                      {question.group}
                    </TextBlock>
                  )}
                  <DiscoveryQuestionField
                    question={question}
                    canEdit={canEdit}
                    onAnswer={(value) => discovery.answer(question.id, value)}
                  />
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
